import { useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import {
  Building2, CircleDollarSign,
  MessageSquare, Calendar,
  ShieldCheck, Compass, Handshake, Wallet, Briefcase, Users, CreditCard,
} from 'lucide-react'
import type { NavGroup } from '@/components/shell/MainNav'
import type { AccessMode as PermAccessMode, SectionKey, PermissionsHash } from '@/types/permissions'
import type { AccessMode as NavAccessMode } from '@/components/shell/MainNav'

interface NavItemDef {
  label: string
  href: string
  icon: typeof Building2
  section: SectionKey
}

const NAV_ITEMS: { group: string; items: NavItemDef[] }[] = [
  {
    group: '_explore',
    items: [
      { label: 'Discover', href: '/discover', icon: Compass, section: 'associations' },
    ],
  },
  {
    group: '_organizations',
    items: [
      { label: 'My Relationships', href: '/relationships', icon: Handshake, section: 'b2b' },
      { label: 'B2B Console', href: '/b2b', icon: Briefcase, section: 'b2b' },
      { label: 'My Associations', href: '/associations', icon: Building2, section: 'associations' },
      { label: 'My Circles', href: '/circles', icon: CircleDollarSign, section: 'circles' },
      { label: 'My Events', href: '/events', icon: Calendar, section: 'communication' },
      { label: 'My Finances', href: '/finances', icon: Wallet, section: 'circles' },
      // M-A2 — un-deferred Credit & Lending (score, payout advances, loans).
      { label: 'My Credits', href: '/credit', icon: CreditCard, section: 'circles' },
    ],
  },
  {
    group: '_communication',
    items: [
      { label: 'Messages', href: '/messages', icon: MessageSquare, section: 'communication' },
      // M-A1 — un-deferred Community (referrals, badges, leaderboard, challenges,
      // stories). Built end-to-end but redirected to /discover with no nav entry.
      { label: 'Community', href: '/community', icon: Users, section: 'communication' },
    ],
  },
  {
    group: '_admin',
    items: [
      { label: 'Admin', href: '/admin', icon: ShieldCheck, section: 'admin' },
    ],
  },
]

function mapAccessMode(mode: PermAccessMode | undefined): NavAccessMode {
  if (!mode || mode === 'none') return 'none'
  if (mode === 'read') return 'read'
  return 'read_write'
}

interface UseNavigationGroupsOptions {
  isCircleManager?: boolean
  /**
   * Gates the B2B Console entry. Must match the backend `/api/v1/b2b`
   * authorization (Mafao employee or platform admin) — a plain Circle
   * Manager without an employee record would 403, so we key off this
   * rather than `isCircleManager`.
   */
  canAccessB2b?: boolean
  badges?: Record<string, number | undefined>
}

export function useNavigationGroups(permissions: PermissionsHash | null, options?: UseNavigationGroupsOptions): NavGroup[] {
  const location = useLocation()
  const isCircleManager = options?.isCircleManager ?? false
  const canAccessB2b = options?.canAccessB2b ?? false
  const badges = options?.badges

  return useMemo(() => {
    return NAV_ITEMS.map(({ group, items }) => ({
      label: group,
      items: items.map((item) => {
        // Relationships + B2B Console use role-based access (no backend
        // permission section yet), so hide them unless the role qualifies.
        const roleGated = item.href === '/relationships' || item.href === '/b2b'
        const hidden =
          (item.href === '/relationships' && !isCircleManager) ||
          (item.href === '/b2b' && !canAccessB2b)

        const accessMode: NavAccessMode = hidden
          ? 'none'
          : roleGated
            ? 'read_write'
            : mapAccessMode(permissions?.[item.section])

        const badge = badges?.[item.href]
        return {
          label: item.label,
          href: item.href,
          icon: item.icon,
          isActive: location.pathname === item.href || (item.href !== '/' && location.pathname.startsWith(item.href)),
          accessMode,
          ...(badge && badge > 0 ? { badge } : {}),
        }
      }),
    }))
  }, [permissions, location.pathname, isCircleManager, canAccessB2b, badges])
}
